import React from 'react';
import { Typography, Box, makeStyles } from '@material-ui/core';
import { t } from 'ttag';

const useStyles = makeStyles((theme) => ({
  scheduleSummaryContainer: {
    borderRadius: '10px',
    width: '100%',
    marginTop: '16px',
    marginBottom: '16px',
  },
  scheduleSummaryHeader: {
    color: 'rgba(255, 255, 255, 1)',
    fontSize: '18px',
    fontWeight: 400,
    marginBottom: '8px',
  },
  timelineEvents: {
    display: 'flex',
    background: 'rgba(255,255,255,0.1)',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '6px',
  },
  scheduleSummaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  scheduleSummaryTime: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: 400,
  },
  scheduleSummaryText: {
    color: 'rgba(255, 255, 255, 1)',
    fontSize: '16px',
    fontWeight: 400,
  },
}));

export interface ScheduleSummaryProps {
  powerOnTime?: string;
  powerOffTime?: string;
  startTimeActive?: boolean;
  endTimeActive?: boolean;
  setpoint?: number | string;
  setpointEnabled?: boolean;
  temperatureSymbol?: string;
  is12Hours?: boolean;
}

const formatTimeForDisplay = (timeStr: string | undefined, is12Hours: boolean = false) => {
  if (!timeStr) return '--:--';

  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(am|pm|AM|PM)?$/i);
  if (!match) return '--:--';

  let [, hours, minutes, ampmPart] = match as any;
  hours = parseInt(hours, 10);
  minutes = parseInt(minutes, 10);

  if (isNaN(hours) || isNaN(minutes)) return '--:--';

  // Normalize to 24-hour format first
  if (ampmPart) {
    const period = ampmPart.toUpperCase();
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
  }

  // Format based on requested style
  if (is12Hours) {
    const period = hours >= 12 ? 'PM' : 'AM';
    let displayHours = hours % 12;
    if (displayHours === 0) displayHours = 12;
    return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  } else {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
};

const ScheduleSummary: React.FC<ScheduleSummaryProps> = (props) => {
  const classes = useStyles();
  const {
    powerOnTime,
    powerOffTime,
    startTimeActive,
    endTimeActive,
    setpoint,
    setpointEnabled,
    temperatureSymbol,
    is12Hours,
  } = props;

  const events: Array<{ time: string | null; dotClass: 'start' | 'off'; action: string }> = [];

  if (startTimeActive) {
    const startActions: string[] = [t`Turn On`];
    if (setpointEnabled && setpoint != null) {
      startActions.push(t`Set to ${setpoint}${temperatureSymbol}`);
    }
    events.push({
      time: formatTimeForDisplay(powerOnTime, is12Hours),
      dotClass: 'start',
      action: startActions.join(', '),
    });
  }

  if (endTimeActive) {
    const endActions: string[] = [t`Turn Off`];
    if (!startTimeActive && setpointEnabled && setpoint != null) {
      endActions.push(t`Set to ${setpoint}${temperatureSymbol}`);
    }
    events.push({
      time: formatTimeForDisplay(powerOffTime, is12Hours),
      dotClass: 'off',
      action: endActions.join(', '),
    });
  }

  // No Turn On/Off selected — show setpoint only if present
  if (!startTimeActive && !endTimeActive && setpointEnabled && setpoint != null) {
    events.push({
      time: null,
      dotClass: 'start',
      action: t`Set to ${setpoint}${temperatureSymbol}`,
    });
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <Box className={classes.scheduleSummaryContainer}>
      <Typography className={classes.scheduleSummaryHeader}>{t`Schedule Summary`}</Typography>
      {events.length > 0 && (
        <div className={classes.timelineEvents}>
          {events.map((event, index) => (
            <div key={index} className={classes.scheduleSummaryRow}>
              {event.time && (
                <Typography className={classes.scheduleSummaryTime}>{event.time}</Typography>
              )}
              <Typography className={classes.scheduleSummaryText}>{event.action}</Typography>
            </div>
          ))}
        </div>
      )}
    </Box>
  );
};

export default ScheduleSummary;
